// Household fixtures: who may manage Michael's account, and whose accounts he asked to manage.
// Jan's request waits on his answer in Family requests, Holly already helps manage his account,
// and his own request to Kevin waits on Kevin. Every answer is applied here, so the next read
// shows it.

import type { HouseholdManagerLink, HouseholdMemberLink } from '~/types/person/household';
import { envelope } from '../envelope';
import { demoDaysAgo, demoDaysAhead } from '../persona';
import type { DemoRoute } from '../types';

const managers: HouseholdManagerLink[] = [
    {
        id: 8101,
        person: { id: 903201, first_name: 'Jan', masked_email: 'j***@dundermifflin.com' },
        status: 'pending',
        since: demoDaysAgo(2),
        expires_at: demoDaysAhead(28),
    },
    {
        id: 8102,
        person: { id: 903202, first_name: 'Holly', masked_email: 'h***@dundermifflin.com' },
        status: 'active',
        since: demoDaysAgo(140),
        expires_at: null,
    },
];

const members: HouseholdMemberLink[] = [
    {
        id: 8201,
        contact_id: 1,
        status: 'active',
        requested_at: demoDaysAgo(141),
        expires_at: null,
        auto_until_first_login: false,
    },
    {
        id: 8202,
        contact_id: 4,
        status: 'pending',
        requested_at: demoDaysAgo(3),
        expires_at: demoDaysAhead(27),
        auto_until_first_login: false,
    },
];

const REQUEST_DAYS = 30;

function managerLink(id: string | undefined): HouseholdManagerLink | undefined {
    return managers.find((link) => link.id === Number(id));
}

export const householdRoutes: DemoRoute[] = [
    { pattern: /^me\/household$/, respond: () => envelope({ managers, members }) },
    {
        pattern: /^me\/household\/(\d+)\/accept$/,
        method: 'POST',
        status: 204,
        respond: (req) => {
            const link = managerLink(req.match[1]);
            if (link) Object.assign(link, { status: 'active', since: demoDaysAgo(0), expires_at: null });
            return envelope(null);
        },
    },
    {
        pattern: /^me\/household\/(\d+)\/decline$/,
        method: 'POST',
        status: 204,
        respond: (req) => {
            const link = managerLink(req.match[1]);
            if (link) Object.assign(link, { status: 'declined', expires_at: null });
            return envelope(null);
        },
    },
    {
        pattern: /^me\/household\/(\d+)\/resend$/,
        method: 'POST',
        status: 204,
        respond: (req) => {
            const link = members.find((held) => held.id === Number(req.match[1]));
            if (link) {
                Object.assign(link, { status: 'pending', requested_at: demoDaysAgo(0), expires_at: demoDaysAhead(REQUEST_DAYS) });
            }
            return envelope(null);
        },
    },
    {
        pattern: /^me\/household\/(\d+)$/,
        method: 'DELETE',
        status: 204,
        respond: (req) => {
            const link = managerLink(req.match[1]) ?? members.find((held) => held.id === Number(req.match[1]));
            if (link) Object.assign(link, { status: 'ended', expires_at: null });
            return envelope(null);
        },
    },
];
